import { displayText } from '../../terminology/store';
// src/Utils/Validations.js
import * as yup from 'yup';
import { parsePhoneNumberFromString } from 'libphonenumber-js';

const isValidEmail = (email) => {
  if (!email) return '';
  if (email.length < 8) return 'Email must be at least 8 characters long.';
  if ((email.match(/@/g) || []).length !== 1) return 'Email must contain exactly one @ symbol.';
  if (!email.includes('.')) return 'Email must contain at least one dot (.).';
  if (/^[.!@}]/.test(email)) return 'Email must not start with a dot (.), exclamation mark (!), at symbol (@), or curly brace (}).';
  if (/\.\./.test(email)) return 'Email cannot contain consecutive dots (e.g., ..).';
  const [beforeAt, afterAt] = email.split('@');
  if (!beforeAt || !afterAt) return 'Email must include a domain after @ (e.g. example.com).';
  if (!/^[a-zA-Z0-9][a-zA-Z0-9._%+-]*[a-zA-Z0-9]$/.test(beforeAt)) {
    return 'Email must include a valid local part before @.';
  }
  if (!afterAt.includes('.')) {
    return 'Email must include a domain after @ (e.g. example.com).';
  }
  const domainParts = afterAt.split('.');
  if (domainParts.length < 2) {
    return 'Email must include a domain after . (e.g. example.com).';
  }
  const domainName = domainParts.slice(0, -1).join('.');
  const domainExtension = domainParts[domainParts.length - 1];
  if (!domainName || !/^[a-zA-Z0-9-]+$/.test(domainName)) {
    return 'Email must include a domain after @ (e.g. example.com).';
  }
  if (!domainExtension) {
    return 'Email must include a domain after . (e.g. example.com).';
  }
  if (domainName.length < 2) return 'Domain name must have at least 2 letters before the dot.';
  if (domainExtension.length < 2) return 'Domain extension must have at least 2 letters after the dot.';
  if (!/^[a-zA-Z]+$/.test(domainExtension)) {
    return 'Email must end with a valid domain extension (e.g., .com, .org) containing only letters.';
  }
  return '';
};

const isValidURL = (url) => {
  if (!url) return '';
  if (!url.startsWith('http://') && !url.startsWith('https://')) return 'URL must start with http:// or https://.';
  try {
    const urlObj = new URL(url);
    const hostname = urlObj.hostname;
    const hostParts = hostname.split('.');
    if (hostParts.length < 2 || hostParts[hostParts.length - 1].length < 2) return 'Invalid URL, must have a valid domain extension like .com';
    return '';
  } catch (_) {
    return 'Invalid URL format.';
  }
};

const nameValidation = (label) => 
  yup.string()
    .required(() => `${displayText(label)} is required.`)
    .min(3, () => `${displayText(label)} must be at least 3 characters long.`)
    .matches(/^[a-zA-Z0-9\s@&().,'-]*$/, () => `${displayText(label)} contains invalid characters.`)
    

const emailValidation = yup.string().test('validate-email', (value, { createError }) => {
  const msg = isValidEmail(value);
  if (msg) return createError({ message: msg });
  return true;
});

const urlValidation = yup.string().test('validate-url', (value, { createError }) => {
  const msg = isValidURL(value);
  if (msg) return createError({ message: msg });
  return true;
});

const dateValidation = yup.string()
  .required('Please select a date.')
  .matches(/^\d{4}-\d{2}-\d{2}$/, 'Invalid date format')
  .test('date-valid', (value, { createError }) => {
    if (!value) return false;
    const [y, m, d] = value.split('-').map(Number);
    if (isNaN(y) || isNaN(m) || isNaN(d)) return createError({ message: 'Invalid date.' });
    if (m < 1 || m > 12) return createError({ message: 'Month must be between 1 and 12.' });
    if (d < 1 || d > 31) return createError({ message: 'Day must be between 1 and 31.' });
    if (y < 1900 || y > new Date().getFullYear()) return createError({ message: 'Year must be between 1900 and current year.' });
    const date = new Date(y, m - 1, d);
    if (date.getDate() !== d || date.getMonth() + 1 !== m || date.getFullYear() !== y) {
      return createError({ message: 'Invalid date.' });
    }
    return true;
  });

// Updated phone validation — no dependency on phone_country
const phoneValidation = yup.string()
  .required('Mobile is required.')
  .test('valid-phone', 'Invalid mobile number.', function (value) {
    if (!value) return false;
    try {
      const phoneNumber = parsePhoneNumberFromString(value);
      return phoneNumber ? phoneNumber.isValid() : false;
    } catch {
      return false;
    }
  });

const passwordValidation = yup.string()
  .required('Password is required.')
  .min(8, 'Password must be at least 8 characters long.')
  .matches(/[A-Z]/, 'Password must contain at least one uppercase letter.')
  .matches(/\d/, 'Password must contain at least one number.')
  .matches(/[^a-zA-Z0-9]/, 'Password must contain at least one special character.');

const basicSchema = yup.object({
  live_transcript_email: emailValidation,
  type: yup.string(),
  level: yup.string(),
  client_type: yup.string(),
  status: yup.string(),
  video_conf: yup.string(),
  reseller_name: nameValidation('Reseller Name'),
  start_date: dateValidation,
  sales_representative: yup.string(),
  primary_contact: nameValidation('Primary Contact'),
  primary_email: emailValidation.required('Primary Email is required.'),
  primary_phone: phoneValidation, // No phone_country needed anymore
  address_country: yup.string().required('Country is required.'),
  address: yup.string().required('Address is required.'),
  city: yup.string().required('City is required.'),
  state: yup.string().required('State is required.'),
  zip: yup.string()
    .required('ZIP is required.')
    .matches(/^[0-9]{5,10}$/, 'ZIP must be a number between 5 and 10 digits.'),
  reseller_description: yup.string(),
  banner_color: yup.string().matches(/^#[0-9A-Fa-f]{6}$/, { message: 'Invalid hex color', excludeEmptyString: true }).nullable(),
  text_color: yup.string().matches(/^#[0-9A-Fa-f]{6}$/, { message: 'Invalid hex color', excludeEmptyString: true }).nullable(),
  logo: yup.mixed().nullable(),
  url: urlValidation,
});

const priceSchema = yup.object({
  subscription_fee: yup.number()
    .typeError('Subscription fee must be a number')
    .required('Subscription fee is required.')
    .min(0, 'Subscription fee must be positive'),
});

const adminSchema = yup.object({
  first_name: nameValidation('First Name'),
  last_name: nameValidation('Last Name'),
  email: emailValidation.required('Email is required.'),
  username: yup.string()
    .required('Username is required.')
    .min(3, 'Username must be at least 3 characters'),
  password: passwordValidation,
  password_confirmation: yup.string()
    .required('Confirm Password is required.')
    .oneOf([yup.ref('password')], 'Passwords do not match'),
});

const formSchema = basicSchema.concat(priceSchema).concat(adminSchema);

export { basicSchema, priceSchema, adminSchema, formSchema };